import { useState, useEffect } from 'react';
import { Phone, Mail, Globe, User, CheckCircle2 } from 'lucide-react';
import type { DriverDetailsData } from './Step1PersonalInformation';
import type { DriverLicenseData } from './Step2LicenseDocument';
import { apiGetFleets } from '../../../api/services/fleetApi';
import type { FleetRecord } from '../../../types';

type Step4Props = {
  driverData: DriverDetailsData;
  licenseData: DriverLicenseData;
  selectedVehicleId: string | null;
};

const InfoRow = ({ label, value, icon: Icon }: { label: string; value: React.ReactNode; icon?: React.ElementType }) => (
  <div className="flex items-center justify-between py-1">
    <span className="font-host text-[14px] font-medium text-[#757575]">{label}</span>
    <span className="font-host text-[14px] font-medium text-[#1A1A1A] flex items-center gap-1.5 text-right">
      {Icon && <Icon size={14} className="text-[#1A1A1A]" />}
      {value}
    </span>
  </div>
);

export const Step4ReviewConfirm = ({ driverData, licenseData, selectedVehicleId }: Step4Props) => {
  const [vehicles, setVehicles] = useState<FleetRecord[]>([]);

  useEffect(() => {
    let mounted = true;
    apiGetFleets().then((res) => {
      if (mounted && res?.data) {
        setVehicles(res.data);
      }
    }).catch(() => {});
    return () => {
      mounted = false;
    };
  }, []);

  const selectedVehicle = vehicles.find((v) => String(v.id) === String(selectedVehicleId));
  const fullName = `${driverData.firstName} ${driverData.lastName}`.trim();

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);

  useEffect(() => {
    if (licenseData.driverPhoto instanceof File) {
      const url = URL.createObjectURL(licenseData.driverPhoto);
      setAvatarUrl(url);
      return () => URL.revokeObjectURL(url);
    } else if (typeof licenseData.driverPhoto === 'string' && licenseData.driverPhoto.trim() !== '') {
      setAvatarUrl(licenseData.driverPhoto);
    } else {
      setAvatarUrl(null);
    }
  }, [licenseData.driverPhoto]);

  const uploadedDocsList = [
    { label: 'Photo', file: licenseData.driverPhoto },
    { label: 'License', file: licenseData.drivingLicense },
    { label: 'Passport/ID', file: licenseData.passportId },
    { label: 'Insurance', file: licenseData.vehicleInsurance },
    { label: 'Police Clearance', file: licenseData.policeClearance },
    { label: 'Medical Cert', file: licenseData.medicalCertificate },
  ];
  const uploadedDocsCount = uploadedDocsList.filter(d => Boolean(d.file)).length;

  return (
    <div className="flex flex-col gap-8 w-full max-w-[1000px] mx-auto px-[32px]">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        
        {/* Top Left: Personal Information */}
        <div className="flex flex-col gap-4">
          <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Personal Information</h3>
          <div className="w-full rounded-[16px] bg-[#EEEEEE] p-5 flex items-start gap-6 min-h-[160px]">
            {/* Avatar */}
            <div className="w-[100px] h-[100px] shrink-0 rounded-[12px] bg-white border border-[#E0E0E0] overflow-hidden flex items-center justify-center shadow-xs">
              {avatarUrl ? (
                <img src={avatarUrl} alt={fullName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-[60px] h-[60px] rounded-full bg-[#1A73E8] flex items-center justify-center">
                  <User size={32} className="text-white" />
                </div>
              )}
            </div>
            
            {/* Details */}
            <div className="flex flex-col flex-1 gap-2 pt-1 w-full">
              <InfoRow label="Driver ID" value={driverData.driverId || 'Auto'} />
              <InfoRow label="Driver name" value={fullName} />
              <InfoRow label="Gender" value={driverData.gender} />
            </div>
          </div>
        </div>

        {/* Top Right: Contact Information */}
        <div className="flex flex-col gap-4">
          <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Contact Information</h3>
          <div className="w-full rounded-[16px] bg-[#EEEEEE] p-6 flex flex-col gap-3 min-h-[160px] justify-center">
            <InfoRow label="Mobile Number" value={driverData.mobileNumber} icon={Phone} />
            <InfoRow label="Email Address" value={driverData.emailAddress} icon={Mail} />
          </div>
        </div>

        {/* Bottom Left: License & Document */}
        <div className="flex flex-col gap-4">
          <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">License & Document</h3>
          <div className="w-full rounded-[16px] bg-[#EEEEEE] p-6 flex flex-col gap-3 min-h-[160px] justify-center">
            <InfoRow label="License Number" value={licenseData.licenseNumber} />
            <InfoRow label="Issue Date" value={licenseData.issueDate} />
            <InfoRow label="Expiry Date" value={licenseData.expiryDate} />
            <InfoRow
              label="Documents Attached"
              value={
                <span className="flex items-center gap-1.5 text-[13px] font-bold text-[#1A1A1A]">
                  <CheckCircle2 size={16} className="text-[#4CAF50]" />
                  {uploadedDocsCount} / 6 Uploaded
                </span>
              }
            />
          </div>
        </div>

        {/* Bottom Right: Vehicle Information */}
        <div className="flex flex-col gap-4">
          <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Vehicle Information</h3>
          <div className="w-full rounded-[16px] bg-[#EEEEEE] p-6 flex flex-col gap-3 min-h-[160px] justify-center">
            <InfoRow label="Registration Number" value="Auto" />
            <InfoRow 
              label="Vehicle Type" 
              value={
                <div className="flex items-center gap-2">
                  <span className="px-3 py-1 bg-white rounded-full text-[12px] font-medium text-[#1A1A1A]">
                    {selectedVehicle?.name}
                  </span>
                </div>
              } 
            />
            <InfoRow label="Languages" value={driverData.languages} icon={Globe} />
          </div>
        </div>

      </div>
    </div>
  );
};